import { motion, useInView, AnimatePresence, useMotionValue, useAnimationFrame } from 'framer-motion';
import { useRef, useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { PROJECTS } from './data/projects';
import ContactModal from './ContactModal';
import '@fontsource/dm-sans/300.css';
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/600.css';
import '@fontsource/dm-serif-display/400.css';
import '@fontsource/dm-serif-display/400-italic.css';
import './App.css';

const ProjectPage = lazy(() => import('./ProjectPage'));
const EduComply   = lazy(() => import('./EduComply'));

// ── Data ──────────────────────────────────────────────────────────────────────
// PROJECTS imported from ./data/projects

const STATS = [
  { value: '15+', label: 'Years leading creative across 50+ global markets' },
  { value: '200M+', label: 'Units sold across the Moose Toys global portfolio' },
  { value: '375%', label: 'YoY Special Feature Plush growth — NPD 2022' },
  { value: '35M+', label: 'Combined influencer reach across Gui Gui creator partnerships' },
];

const AWARDS = [
  // Magic Mixies — creative & campaign recognition
  { title: 'TOTY Creative Toy of the Year', project: 'Magic Mixies', year: '2022 · 2023' },
  { title: 'TOTY Silver\nMarketing Campaign', project: 'Magic Mixies Crystal Ball', year: '2023' },
  // Little Live Pets — brand & retail marketing driven
  { title: 'DreamToys\nTop Toy', project: 'Little Live Pets', year: 'UK · 2022 & 2023' },
  // Bluey — brand campaign & licensing recognition
  { title: 'TOTY License of the Year', project: 'Bluey', year: '2025' },
  { title: 'Best Licensed Toys & Games Range', project: 'Bluey · British Licensing Awards', year: '2022' },
  { title: 'ATA Licensed Toy of the Year', project: 'Bluey', year: '2021 · 2022' },
  { title: '#1 Preschool Licence\nUK & Australia', project: 'Bluey · Circana', year: '2023' },
  // Shopkins — brand marketing recognition
  { title: 'TOTY Girl\nToy of the Year', project: 'Shopkins', year: '2015 · 2016' },
];

const SKILLS = [
  'Art Direction',
  'Brand Systems & Style Guides',
  'Motion & Video Production',
  'Digital · Social · Ecommerce',
  'Retail POS & OOH',
  'Global Campaign Leadership',
  'Creative Mentorship',
  'Trade Show & Experiential',
];

const CLIENTS = [
  'Moose Toys', 'Shopkins', 'Little Live Pets', 'Magic Mixies',
  'Gui Gui', 'Pikmi Pops', 'Bluey', 'Nike',
  'VicHealth', 'CSIRO', 'Australian Unity', 'Berry Street',
  'NBN Co', 'Airnorth Airlines', 'WeMov', 'Adverto',
  'National Australia Bank', 'Bega Dairy', 'Bond University',
  'Zookie', 'HistoryWOW', 'E24',
];

// ── Motion Graphics videos ────────────────────────────────────────────────────
// Replace youtubeId values with your actual YouTube video IDs

const MOTION_VIDEOS = [
  {
    id: 'mg-15',
    youtubeId: 'vDDthC3Kl0E',
    title: 'The Sunday Guardian — On Air Promo',
    brand: 'The Sunday Guardian',
    blurb: 'Digital video produced to promote The Sunday Guardian — India\'s finest Sunday newspaper — across national television networks. Broadcast creative built to communicate the publication\'s authority and editorial prestige at scale.',
    thumbScale: 140,
  },
  {
    id: 'mg-07',
    youtubeId: 'Qnd7RAwBTzg',
    startDelay: 3000,
    title: 'Digital Australia — Motion Graphics',
    brand: 'Digital Media Campaigns',
    blurb: 'Motion graphics produced for digital media campaigns across Australia — spanning animated content, brand storytelling, and digital-first creative for Australian audiences.',
  },
  {
    id: 'mg-05',
    youtubeId: 'dgsCQRb18SU',
    title: 'Motion Graphics Showreel',
    brand: 'Indian National Broadcast Network',
    blurb: 'A showreel of motion graphics work produced for Indian national broadcast — spanning on air promos, title sequences, and channel branding across news and entertainment formats.',
  },
  {
    id: 'mg-01',
    youtubeId: 'sSFJZR7tKmM',
    title: 'Remembering Diana — On Air Promo',
    brand: 'NewsX · National Broadcast English News Channel, India',
    blurb: 'On air promo produced for NewsX, India\'s national English news channel. Crafted for broadcast, balancing editorial gravitas with motion design to mark the legacy of Princess Diana.',
  },
  {
    id: 'mg-02',
    youtubeId: '8x0rYUsTEDI',
    title: 'IPL with Mayanti Langer — On Air Promo',
    brand: 'NewsX · National Broadcast English News Channel, India',
    blurb: 'On air promo for NewsX\'s IPL coverage featuring Mayanti Langer. Produced for national broadcast, blending sports energy with the visual language of premium news television.',
    thumbScale: 130,
  },
  {
    id: 'mg-04',
    youtubeId: 'KgzDM1QMIeU',
    title: 'NBN — Motion Campaign',
    brand: 'NBN Co · National Broadband Network, Australia',
    blurb: 'Motion graphics produced for NBN Co, Australia\'s national broadband network. Campaign creative designed to communicate infrastructure at scale to a broad national audience.',
  },
  {
    id: 'mg-06',
    youtubeId: 'Noh5H_MuAMo',
    title: 'NBN — Motion Campaign',
    brand: 'NBN Co · National Broadband Network, Australia',
    blurb: 'Motion graphics produced for NBN Co, Australia\'s national broadband network. Campaign creative designed to communicate infrastructure at scale to a broad national audience.',
  },
  {
    id: 'mg-08',
    youtubeId: 'F_WVOKwuvSc',
    title: 'Cinematic Marvels — On Air Promo',
    brand: 'NewsX · National Broadcast English News Channel, India',
    blurb: 'On air promo produced for NewsX celebrating cinematic milestones. Motion-led editorial design crafted for national broadcast — blending film culture with the visual language of news television.',
    thumbScale: 140,
  },
  {
    id: 'mg-09',
    youtubeId: 'KKXok6NFqWg',
    title: 'The High Performance Mindset at Work',
    brand: 'Digital Presentation & Infographics',
    blurb: 'Motion-led digital presentation exploring high performance thinking in the workplace. Data visualisation and infographic design translated into a cohesive animated format.',
  },
  {
    id: 'mg-10',
    youtubeId: 'TaUTJJiGjec',
    title: 'Violence Against Women — Campaign',
    brand: 'VicHealth',
    blurb: 'Motion graphics for VicHealth\'s Violence Against Women campaign. Purpose-led creative designed to shift attitudes and drive awareness across digital and broadcast channels in Victoria.',
  },
  {
    id: 'mg-11',
    youtubeId: '0bvLtsfMDjg',
    title: 'WeMov — Website Explainer',
    brand: 'WeMov',
    blurb: 'Animated website explainer for WeMov. Motion graphics and infographic storytelling used to communicate the product proposition clearly and compellingly for a digital audience.',
  },
  {
    id: 'mg-12',
    youtubeId: '4lC7u_eDQFY',
    title: 'Biosecurity Flagship — Campaign Launch',
    brand: 'CSIRO',
    blurb: 'Motion graphics for the launch of CSIRO\'s Biosecurity Flagship campaign. Science communication at national scale — translating complex research into clear, compelling visual storytelling.',
  },
  {
    id: 'mg-13',
    youtubeId: 'QSH9hgStS9g',
    title: 'Renters Card — Animated Explainer',
    brand: 'Renters Card',
    blurb: 'Animated explainer video for Renters Card. Motion graphics and character animation used to break down a financial product into a clear, engaging narrative for a consumer audience.',
  },
  {
    id: 'mg-14',
    youtubeId: '3LEFLE-e3Kk',
    title: 'Wimbledon Bonanza — On Air Promo',
    brand: 'NewsX · National Broadcast English News Channel, India',
    blurb: 'On air promo for NewsX\'s Wimbledon coverage. High-energy broadcast creative designed to capture the prestige of the tournament and drive tune-in across national English news audiences.',
    thumbScale: 140,
  },
];

// ── Animation helpers ─────────────────────────────────────────────────────────

const EASE = [0.22, 1, 0.36, 1];

function fadeUp(delay = 0) {
  return {
    initial: { opacity: 0, y: 28 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.78, delay, ease: EASE },
  };
}

function useReveal(margin = '-8%') {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin });
  return { ref, inView };
}

// ── Nav ───────────────────────────────────────────────────────────────────────

function Nav({ onContactClick, onServicesClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 64);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  useEffect(() => {
    if (open) {
      const w = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = `${w}px`;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.paddingRight = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <motion.nav
        className={`nav${scrolled ? ' nav--scrolled' : ''}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.2, ease: EASE }}
      >
        <a href="#hero" className="nav-logo" onClick={close}>SS</a>

        {/* Desktop links */}
        <div className="nav-menu nav-menu--desktop">
          <a href="#work">Work</a>
          <a href="#about" onClick={() => window.dispatchEvent(new CustomEvent('enable-hero-sound'))}>About</a>
          <button className="nav-services-trigger" onClick={onServicesClick}>Services</button>
          <button className="nav-contact-trigger" onClick={onContactClick}>Contact</button>
        </div>

        <button
          className="nav-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen(o => !o)}
        >
          <span className={`bar${open ? ' open' : ''}`} />
          <span className={`bar${open ? ' open' : ''}`} />
        </button>
      </motion.nav>

      {/* Mobile overlay — only mounted when open, no layout impact when closed */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="nav-mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="nmo-inner">
              <a href="#work" onClick={close} className="nmo-link">Work</a>
              <div className="nmo-divider" />
              <a href="#about" onClick={() => { close(); window.dispatchEvent(new CustomEvent('enable-hero-sound')); }} className="nmo-link">About</a>
              <div className="nmo-divider" />
              <button onClick={() => { close(); onServicesClick(); }} className="nmo-link">Services</button>
              <div className="nmo-divider" />
              <button onClick={() => { close(); onContactClick(); }} className="nmo-link">Contact</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ── Hero Canvas ───────────────────────────────────────────────────────────────

// ── Hero ──────────────────────────────────────────────────────────────────────

function Hero({ onContactClick }) {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  // Force play on mount (needed for iOS Safari)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {});
  }, []);

  // Unmute when About nav link clicked (user gesture required by browser)
  useEffect(() => {
    function handleEnableSound() {
      const v = videoRef.current;
      if (!v) return;
      v.muted = false;
      v.volume = 0.25;
      setSoundOn(true);
    }
    window.addEventListener('enable-hero-sound', handleEnableSound);
    return () => window.removeEventListener('enable-hero-sound', handleEnableSound);
  }, []);

  // Mute when hero scrolls out of view
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          const v = videoRef.current;
          if (v) v.muted = true;
          setSoundOn(false);
        }
      },
      { threshold: 0.8 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  function toggleSound() {
    const v = videoRef.current;
    if (!v) return;
    if (soundOn) {
      v.muted = true;
      setSoundOn(false);
    } else {
      v.muted = false;
      v.volume = 0.25;
      setSoundOn(true);
    }
  }

  return (
    <section className="hero" id="hero" ref={sectionRef}>
      {/* Full-bleed background video */}
      <video
        preload="none"
        ref={videoRef}
        className="hero-bg-video"
        src="/projects/hero.mp4"
        autoPlay
        loop
        muted
        playsInline
        style={{ pointerEvents: 'none' }}
      />
      {/* Overlay so text stays readable */}
      <div className="hero-bg-overlay" />

      {/* Click overlay — restart from frame 0 + toggle sound */}
      <div
        className="hero-click-overlay"
        onClick={() => {
          const v = videoRef.current;
          if (!v) return;
          v.currentTime = 0;
          v.play();
          toggleSound();
        }}
      />

      {/* Sound toggle */}
      <motion.button
        className={`hero-sound-toggle${soundOn ? ' on' : ''}`}
        onClick={toggleSound}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.6 }}
        aria-label={soundOn ? 'Mute' : 'Unmute'}
      >
        {soundOn ? (
          /* Speaker with waves */
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
          </svg>
        ) : (
          /* Speaker muted */
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <line x1="23" y1="9" x2="17" y2="15"/>
            <line x1="17" y1="9" x2="23" y2="15"/>
          </svg>
        )}
        <span>{soundOn ? 'Sound On' : 'Sound Off'}</span>
      </motion.button>

      <div className="hero-content">
        <motion.span className="eyebrow" {...fadeUp(0.3)}>
          Art Director<br />Sr Designer / Creative Lead
        </motion.span>

        <motion.h1 className="hero-name" {...fadeUp(0.46)}>
          Sahil<br />Saini
        </motion.h1>

        <motion.p className="hero-sub" {...fadeUp(0.62)}>
          15+ years helping brands look sharper, move faster, and stay consistent —
          from social and ecommerce to retail, OOH, and video.
        </motion.p>

        <motion.div className="hero-btns" {...fadeUp(0.78)}>
          <div className="hero-btns-row">
            <a href="#work" className="btn-primary">View Work</a>
            <button className="btn-ghost" onClick={() => onContactClick()}>
              Get in Touch
            </button>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="hero-scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
      >
        <span className="scroll-bar" />
        <span className="scroll-word">Scroll</span>
      </motion.div>
    </section>
  );
}

// ── Awards Wall of Fame ───────────────────────────────────────────────────────

const AWARDS_FEATURED = new Set([0, 1]); // indices of winner-tier awards

function AwardsWall() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <div className="awards-wall" ref={ref}>
      <div className="awards-wall-header">
        <div className="awards-header-rule" />
        <span className="eyebrow awards-eyebrow">Wall of Fame</span>
        <div className="awards-header-rule" />
      </div>

      <div className="awards-wall-list">
        {AWARDS.map((a, i) => (
          <motion.div
            key={i}
            className={`aw-row${AWARDS_FEATURED.has(i) ? ' aw-row--featured' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: i * 0.07, ease: EASE }}
          >
            <span className="aw-num">{String(i + 1).padStart(2, '0')}</span>
            <div className="aw-body">
              <h3 className="aw-title">{a.title}</h3>
              <div className="aw-sub">
                <span className="aw-brand">{a.project}</span>
                <span className="aw-sep">·</span>
                <span className="aw-year">{a.year}</span>
              </div>
            </div>
            {AWARDS_FEATURED.has(i) && (
              <span className="aw-badge">★ Winner</span>
            )}
          </motion.div>
        ))}
      </div>

      <p className="awards-disclaimer" style={{ padding: '20px var(--pad) 0' }}>
        ※ Awards presented to Moose Toys. I contributed as Art Director, Global Marketing and Creative Lead across these campaigns.
      </p>
    </div>
  );
}

// ── Awards Module ─────────────────────────────────────────────────────────────

// Duplicate entries so the ticker always fills the strip
const AWARDS_TICKER = [...AWARDS, ...AWARDS];

function AwardsModule() {
  const tickerRef  = useRef(null);
  const halfWidth  = useRef(0);
  const isDragging = useRef(false);
  const x          = useMotionValue(0);

  // Measure half-width after mount
  useEffect(() => {
    if (tickerRef.current) {
      halfWidth.current = tickerRef.current.scrollWidth / 2;
    }
  }, []);

  // Continuous scroll loop — runs every animation frame
  useAnimationFrame((_, delta) => {
    if (isDragging.current) return;
    const half = halfWidth.current;
    let next = x.get() - (delta / 16.67) * 0.7; // ~0.7px per frame at 60fps
    if (half > 0 && next <= -half) next += half;
    x.set(next);
  });

  const handleDragStart = () => { isDragging.current = true; };

  const handleDragEnd = () => {
    // Normalise position so loop stays seamless after drag
    const half = halfWidth.current;
    if (half > 0) {
      let cur = x.get();
      while (cur <= -half) cur += half;
      while (cur > 0)      cur -= half;
      x.set(cur);
    }
    isDragging.current = false;
  };

  return (
    <section className="awards-module">
      <div className="awards-header">
        <div className="awards-header-rule" />
        <span className="eyebrow awards-eyebrow">Awards</span>
        <div className="awards-header-rule" />
      </div>
      {/* Running strip */}
      <div className="awards-ticker-wrap">
        <motion.div
          ref={tickerRef}
          className="awards-ticker"
          style={{ x }}
          drag="x"
          dragConstraints={false}
          dragElastic={0}
          dragMomentum={false}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {AWARDS_TICKER.map((a, i) => (
            <span key={i} className="awards-ticker-item">
              <span className="awards-ticker-divider" aria-hidden="true" />
              <span className="awards-ticker-content">
                <span className="awards-ticker-star">★</span>
                <span className="awards-ticker-body">
                  <span className="awards-ticker-title">
                  {a.title.split('\n').map((line, j) => (
                    <span key={j}>{line}{j < a.title.split('\n').length - 1 && <br />}</span>
                  ))}
                </span>
                  <span className="awards-ticker-meta">{a.project} · {a.year}</span>
                </span>
              </span>
            </span>
          ))}
        </motion.div>
      </div>

      {/* Bottom rule with disclaimer */}
      <div className="awards-header awards-footer-rule">
        <div className="awards-header-rule" />
        <span className="awards-disclaimer awards-disclaimer--inline">※ Awards presented to Moose Toys. I contributed as Art Director, Global Marketing and Creative Lead across these campaigns.</span>
        <div className="awards-header-rule" />
      </div>
    </section>
  );
}

// ── Intro Reel ────────────────────────────────────────────────────────────────

function IntroReel() {
  const containerRef = useRef(null);
  const inViewRef = useRef(null);
  const inView = useInView(inViewRef, { once: true, margin: '-10%' });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const [muted, setMuted] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setMuted(vid.muted);
  };

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onTimeUpdate = () => {
      if (vid.duration) setProgress(vid.currentTime / vid.duration);
    };
    vid.addEventListener('timeupdate', onTimeUpdate);
    return () => vid.removeEventListener('timeupdate', onTimeUpdate);
  }, []);

  // Pause + mute when scrolled out of view
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          vid.pause();
          vid.muted = true;
          setMuted(true);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(vid);
    return () => observer.disconnect();
  }, []);


  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let width, height, raf;

    const resize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };
    resize();
    window.addEventListener('resize', resize);

    const COUNT = 72;
    const CONNECT = 140;
    const MOUSE_R = 110;
    // Safe zone: left 52% width, top 78% height (avoids face on right, text at bottom)
    const SAFE_X = () => Math.random() * width * 0.52;
    const SAFE_Y = () => Math.random() * height * 0.78;

    const pts = Array.from({ length: COUNT }, () => ({
      x: SAFE_X(),
      y: SAFE_Y(),
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
      r: Math.random() * 1.6 + 0.8,
      gold: Math.random() > 0.65,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const { x: mx, y: my } = mouseRef.current;

      pts.forEach(p => {
        const dx = p.x - mx, dy = p.y - my;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < MOUSE_R) {
          const f = ((MOUSE_R - d) / MOUSE_R) * 0.9;
          p.vx += (dx / d) * f;
          p.vy += (dy / d) * f;
        }
        p.vx *= 0.97; p.vy *= 0.97;
        const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (spd > 2.2) { p.vx = (p.vx / spd) * 2.2; p.vy = (p.vy / spd) * 2.2; }
        p.x += p.vx; p.y += p.vy;
        const maxX = width * 0.52;
        const maxY = height * 0.78;
        if (p.x < 0 || p.x > maxX) { p.vx *= -1; p.x = Math.max(0, Math.min(maxX, p.x)); }
        if (p.y < 0 || p.y > maxY) { p.vy *= -1; p.y = Math.max(0, Math.min(maxY, p.y)); }
      });

      // particle–particle lines
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i], b = pts[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT) {
            const alpha = (1 - d / CONNECT) * 0.3;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(201,168,76,${alpha})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
        // lines to mouse
        const p = pts[i];
        const dx = p.x - mx, dy = p.y - my;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < CONNECT * 1.6) {
          const alpha = (1 - d / (CONNECT * 1.6)) * 0.55;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(mx, my);
          ctx.strokeStyle = `rgba(201,168,76,${alpha})`;
          ctx.lineWidth = 0.9;
          ctx.stroke();
        }
      }

      // dots
      pts.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.gold ? 'rgba(201,168,76,0.85)' : 'rgba(240,236,227,0.45)';
        ctx.fill();
      });

      // cursor dot
      if (mx > 0) {
        ctx.beginPath();
        ctx.arc(mx, my, 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(201,168,76,0.6)';
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="intro-reel-section" id="about">
      <div className="intro-reel-section-head">
        <span className="eyebrow">About me</span>
        <h2 className="section-title">Fifteen years of work that earns its place.</h2>
      </div>
    <div className="intro-reel-row" ref={(el) => { containerRef.current = el; inViewRef.current = el; }}>

      {/* Video */}
      <div
        className="intro-reel"
        onClick={() => {
          const v = videoRef.current;
          if (!v || videoError) return;
          v.currentTime = 0;
          v.play();
          toggleMute();
        }}
        style={{ cursor: 'pointer' }}
      >
        {videoError ? (
          <div className="intro-reel-fallback">
            <div className="intro-reel-fallback-inner">
              <span className="intro-reel-fallback-label">In My Own Words</span>
              <span className="intro-reel-fallback-text">Walkthrough reel — coming soon</span>
            </div>
          </div>
        ) : (
          <video
            preload="none"
            ref={videoRef}
            className="intro-reel-video"
            src="/projects/intro.mp4"
            autoPlay
            muted
            playsInline
            style={{ pointerEvents: 'none' }}
            onError={() => setVideoError(true)}
            onEnded={() => {
              const vid = videoRef.current;
              if (vid) { vid.muted = true; }
              setMuted(true);
              setTimeout(() => {
                document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' });
              }, 400);
            }}
          />
        )}

        <motion.div
          className="intro-reel-text"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="intro-reel-title">Sahil Saini</p>
          <span className="intro-reel-meta">Sr Designer / Art Director</span>
        </motion.div>
        <button
          className={`intro-reel-toggle${videoError ? ' intro-reel-toggle--disabled' : ''}`}
          onClick={(e) => { e.stopPropagation(); if (!videoError) toggleMute(); }}
          disabled={videoError}
          aria-label={muted ? 'Turn audio on' : 'Turn audio off'}
        >
          {muted ? (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
              </svg>
              Sound Off
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              Sound On
            </>
          )}
        </button>

        {/* Scrub bar */}
        {!videoError && (
          <div className="intro-reel-scrub" onClick={e => e.stopPropagation()}>
            <input
              type="range"
              className="intro-reel-scrub-input"
              min="0"
              max="1"
              step="0.001"
              value={progress}
              style={{ '--progress': progress }}
              onChange={e => {
                const vid = videoRef.current;
                if (!vid || !vid.duration) return;
                const val = parseFloat(e.target.value);
                vid.currentTime = val * vid.duration;
                setProgress(val);
              }}
              aria-label="Video progress"
            />
          </div>
        )}
      </div>

      {/* Metrics panel */}
      <motion.div
        className="intro-reel-metrics"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <p className="sidebar-heading">By the numbers</p>
        <ul className="sidebar-metrics">
          {STATS.map((s, i) => (
            <li key={i} className="sidebar-metric">
              <span className="sidebar-metric-val">{s.value}</span>
              <span className="sidebar-metric-lbl">{s.label}</span>
            </li>
          ))}
        </ul>
      </motion.div>

    </div>
    </div>
  );
}

// ── Work ──────────────────────────────────────────────────────────────────────

function Work() {
  const { ref, inView } = useReveal();
  const [tab, setTab] = useState(0);

  return (
    <section className="work" id="work">
      <div className="section-head" ref={ref}>
        <motion.span
          className="eyebrow"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
        >
          My Work
        </motion.span>
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
        >
          Campaigns, brands, and systems built across 50+ markets.
        </motion.h2>

        <motion.div
          className="work-tabs"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.28 }}
        >
          {['Brand Systems', 'Motion Graphics', 'AI Projects & Workflow'].map((label, i) => (
            <button
              key={label}
              className={`tab-btn${tab === i ? ' active' : ''}`}
              onClick={() => setTab(i)}
            >
              {label}
              {tab === i && (
                <motion.span
                  className="tab-indicator"
                  layoutId="work-tab-indicator"
                  transition={{ duration: 0.28, ease: EASE }}
                />
              )}
            </button>
          ))}
        </motion.div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.32, ease: EASE }}
        >
          {tab === 0 && (
            <div className="projects-grid">
              {PROJECTS.filter(p => !p.hidden).map((p, i) => (
                <ProjectCard key={p.id} project={p} index={i} />
              ))}
              <div className="projects-coming-soon">
                <span className="projects-coming-soon-dot" />
                <span>More projects coming soon</span>
              </div>
            </div>
          )}
          {tab === 1 && <MotionTab />}
          {tab === 2 && <AITab />}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}

function CardThumb({ project }) {
  const previews = project.previewImages;
  const [idx, setIdx] = useState(0);

  // Deduplicate previews so nothing shows twice
  const uniquePreviews = previews
    ? previews.filter((src, i, arr) => arr.indexOf(src) === i)
    : null;

  useEffect(() => {
    if (!uniquePreviews || uniquePreviews.length < 2) return;
    const isVideo = uniquePreviews[idx] && uniquePreviews[idx].endsWith('.mp4');
    // Hold videos a bit longer than stills
    const delay = isVideo ? 4000 : 2400;
    const id = setTimeout(() => {
      setIdx(i => (i + 1) % uniquePreviews.length);
    }, delay);
    return () => clearTimeout(id);
  }, [uniquePreviews, idx]);

  if (!uniquePreviews && !project.heroVideo && !project.heroImage) return null;

  const currentSrc = uniquePreviews ? uniquePreviews[idx] : null;
  const isVideo = currentSrc && currentSrc.endsWith('.mp4');

  return (
    <div className="pcard-thumb">
      {uniquePreviews && uniquePreviews.length > 0 ? (
        <AnimatePresence mode="sync" initial={false}>
          {isVideo ? (
            <motion.video
              key={currentSrc}
              src={currentSrc}
              autoPlay loop muted playsInline
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <motion.img
              key={currentSrc}
              src={currentSrc}
              alt={project.title}
              loading="lazy"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </AnimatePresence>
      ) : project.heroVideo ? (
        <video
          preload="none"
          src={project.heroVideo}
          autoPlay loop muted playsInline
          onLoadedMetadata={e => { if (project.videoStartTime) e.target.currentTime = project.videoStartTime; }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <img src={project.heroImage} alt={project.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: project.heroImagePosition || 'center' }} />
      )}
      <div className="pcard-thumb-fade" />
    </div>
  );
}

function ProjectCard({ project, index }) {
  const { ref, inView } = useReveal('-4%');
  const soon = project.comingSoon;

  return (
    <motion.div
      ref={ref}
      className={`pcard-wrap${soon ? ' pcard-wrap--soon' : ''}`}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: (index % 2) * 0.1, ease: EASE }}
      whileHover={!soon ? { y: -6, transition: { duration: 0.28 } } : {}}
    >
      {soon ? (
        <div className="pcard pcard--soon" style={{ '--hue': project.hue }}>
          <CardThumb project={project} />
          <div className="pcard-body">
            <div className="pcard-meta">
              <span className="pcard-cat">{project.category}</span>
              <span className="pcard-soon-badge">Coming soon</span>
            </div>
            <h3 className="pcard-title">{project.title}</h3>
          </div>
        </div>
      ) : (
      <Link
        to={`/project/${project.slug}`}
        className="pcard"
        style={{ '--hue': project.hue }}
      >
        <CardThumb project={project} />
        <div className="pcard-body">
          <div className="pcard-meta">
            <span className="pcard-cat">{project.category}</span>
            <span className="pcard-arrow">↗</span>
          </div>
          <h3 className="pcard-title">{project.title}</h3>
          {project.stat && (
            <div className="pcard-footer">
              <span className="pcard-stat">{project.stat}</span>
            </div>
          )}
        </div>
      </Link>
      )}
    </motion.div>
  );
}

// ── Motion Workflow Carousel ──────────────────────────────────────────────────

const MOTION_WORKFLOW_IMGS = [
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-01.webp', label: 'Storyboard & Movement Study' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-02.webp', label: 'AI Shot Development' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-03.webp', label: 'Character & Costume Design' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-4.webp',  label: 'Hero Image — Ignition Swing' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-05.webp', label: 'Motion Frame — Forward Thrust' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-06.webp', label: 'Motion Frame — Rising Wave' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-07.webp', label: 'Motion Frame — Circular Spin' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-08.webp', label: 'Motion Frame — Pressure Blast' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-09.webp', label: 'Motion Frame — Spiral Wrap' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-10.webp', label: 'Motion Frame — Tension Pullback' },
  { src: '/projects/AI Projects & Workflow/04Motion/workflow-11.webp', label: 'Motion Frame — Final Impact' },
];


function MotionWorkflowCarousel({ current, setCurrent, direction, setDirection }) {
  const [paused, setPaused] = useState(false);
  const swipeStartX = useRef(null);
  const total = MOTION_WORKFLOW_IMGS.length;

  const navigate = (dir) => {
    setDirection(dir);
    setCurrent(prev => (prev + dir + total) % total);
  };

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setDirection(1);
      setCurrent(prev => (prev + 1 + total) % total);
    }, 4000);
    return () => clearInterval(t);
  }, [paused, total, setCurrent, setDirection]);

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? '-40%' : '40%', opacity: 0 }),
  };

  const item = MOTION_WORKFLOW_IMGS[current];

  return (
    <div
      className="mwc"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => { setPaused(false); swipeStartX.current = null; }}
      onPointerDown={e => { swipeStartX.current = e.clientX; setPaused(true); }}
      onPointerUp={e => {
        if (swipeStartX.current === null) return;
        const dx = e.clientX - swipeStartX.current;
        swipeStartX.current = null;
        setPaused(false);
        if (Math.abs(dx) > 40) navigate(dx < 0 ? 1 : -1);
      }}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Slide track */}
      <div className="mwc-track">
        <AnimatePresence custom={direction} mode="sync">
          <motion.div
            key={current}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: EASE }}
            className="mwc-slide"
          >
            <img
              src={item.src}
              alt={item.label}
              className="mwc-img"
              loading="lazy"
            />
          </motion.div>
        </AnimatePresence>


        {/* Arrows */}
        <button className="mwc-arrow mwc-arrow--prev" onClick={() => navigate(-1)} aria-label="Previous">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <button className="mwc-arrow mwc-arrow--next" onClick={() => navigate(1)} aria-label="Next">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>

      {/* Dots overlay */}
      <div className="mwc-dots">
        {MOTION_WORKFLOW_IMGS.map((_, i) => (
          <button
            key={i}
            className={`mwc-dot${i === current ? ' active' : ''}`}
            onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function MotionVideoCell() {
  const [muted, setMuted] = useState(true);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const videoRef = useRef(null);
  const { ref, inView } = useReveal('-6%');
  const item = AI_GALLERY.find(it => it.step === '04');
  const total = MOTION_WORKFLOW_IMGS.length;

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setMuted(vid.muted);
  };

  return (
    <motion.div
      ref={ref}
      className="ai-gcell ai-gcell--full ai-gcell--motion04"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.72, ease: EASE }}
    >
      <div className="motion04-body">
        {/* Left: video */}
        <div className="motion04-video-wrap">
          <video
            preload="none"
            ref={videoRef}
            src={item.video}
            autoPlay loop muted playsInline
            className="motion04-video"
          />
          <div className="ai-gcell-label">
            <span className="ai-gcell-name">Motion &amp; Video</span>
          </div>
        </div>

        {/* Right: workflow carousel */}
        <div className="motion04-carousel-wrap">
          <MotionWorkflowCarousel current={current} setCurrent={setCurrent} direction={direction} setDirection={setDirection} />
        </div>
      </div>

      {/* Tools footer */}
      {item.tools && (
        <div className="ai-gcell-footer">
          <div className="ai-tool-tabs">
            <span className="ai-tools-label">Tools I Use</span>
            {item.tools.map((t, i) => (
              <span key={i} className="ai-tool-tab">{t.name}</span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ── AI Storyboard Carousel ────────────────────────────────────────────────────

const AI_STORYBOARDS = [
  '/projects/AI Projects & Workflow/01ConceptandIdeation/Storyborad 01.webp',
  '/projects/AI Projects & Workflow/01ConceptandIdeation/Storyborad 02.webp',
  '/projects/AI Projects & Workflow/01ConceptandIdeation/Storyborad 03.webp',
  '/projects/AI Projects & Workflow/01ConceptandIdeation/Storyborad 04.webp',
  '/projects/AI Projects & Workflow/01ConceptandIdeation/Storyborad 05.webp',
];

function AIStoryboardCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const swipeStartX = useRef(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const count = AI_STORYBOARDS.length;

  const lightboxPrev = () => setLightboxIdx(i => (i - 1 + count) % count);
  const lightboxNext = () => setLightboxIdx(i => (i + 1) % count);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const prev = () => setLightboxIdx(i => (i - 1 + count) % count);
    const next = () => setLightboxIdx(i => (i + 1) % count);
    const onKey = (e) => {
      if (e.key === 'Escape')     setLightboxIdx(null);
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, count]);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive(prev => (prev + 1) % count);
    }, 3000);
    return () => clearInterval(timer);
  }, [paused, count]);

  const getCardProps = (i) => {
    let offset = i - active;
    if (offset > count / 2) offset -= count;
    if (offset < -count / 2) offset += count;
    const abs = Math.abs(offset);
    const sign = Math.sign(offset) || 1;
    if (abs === 0) return { x: 0,         rotateY: 0,          scale: 1,    opacity: 1,    zIndex: 10, brightness: 1    };
    if (abs === 1) return { x: sign * 200, rotateY: sign * -50, scale: 0.78, opacity: 0.75, zIndex: 5,  brightness: 0.65 };
                   return { x: sign * 320, rotateY: sign * -72, scale: 0.58, opacity: 0.35, zIndex: 1,  brightness: 0.45 };
  };

  return (
    <>
    <div className="ai-sb-inner">
      {/* Copy — left column */}
      <div className="ai-sb-copy">
        <h2 className="ai-sb-heading">Brief to board.</h2>
        <p className="ai-sb-body">Visual direction set before production opens.</p>
        <div className="ai-sb-benefits">
          <div className="ai-sb-benefit">
            <span className="ai-sb-benefit-icon">↑</span>
            <span className="ai-sb-benefit-title">Speed</span>
          </div>
          <div className="ai-sb-benefit">
            <span className="ai-sb-benefit-icon">↓</span>
            <span className="ai-sb-benefit-title">Revisions</span>
          </div>
          <div className="ai-sb-benefit">
            <span className="ai-sb-benefit-icon">↓</span>
            <span className="ai-sb-benefit-title">Cost</span>
          </div>
        </div>
      </div>

      {/* Coverflow carousel — right column, full height */}
      <div
        className="ai-sb-stage"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => { setPaused(false); swipeStartX.current = null; }}
        onPointerDown={e => { swipeStartX.current = e.clientX; setPaused(true); }}
        onPointerUp={e => {
          if (swipeStartX.current === null) return;
          const dx = e.clientX - swipeStartX.current;
          swipeStartX.current = null;
          setPaused(false);
          if (Math.abs(dx) > 40) setActive(p => dx < 0 ? (p + 1) % count : (p - 1 + count) % count);
        }}
        style={{ touchAction: 'pan-y' }}
      >
        <div className="ai-sb-scene">
          {AI_STORYBOARDS.map((src, i) => {
            const { x, rotateY, scale, opacity, zIndex, brightness } = getCardProps(i);
            return (
              <motion.div
                key={i}
                className="ai-sb-card"
                animate={{ x, rotateY, scale, opacity }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                style={{ zIndex, cursor: i === active ? 'zoom-in' : 'pointer' }}
                onClick={() => i === active ? setLightboxIdx(i) : setActive(i)}
                whileHover={i === active ? { scale: 1.03 } : {}}
              >
                <motion.img
                  src={src}
                  alt={`Concept ${i + 1}`}
                  loading="lazy"
                  animate={{ filter: `brightness(${brightness})` }}
                  transition={{ duration: 0.65 }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Dot indicators */}
        <div className="ai-sb-dots">
          {AI_STORYBOARDS.map((_, i) => (
            <button
              key={i}
              className={`ai-sb-dot${i === active ? ' active' : ''}`}
              onClick={() => setActive(i)}
              aria-label={`Go to storyboard ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>

    {/* Lightbox */}
    <AnimatePresence>
      {lightboxIdx !== null && (
        <motion.div
          className="sb-lightbox-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => setLightboxIdx(null)}
        >
          <button className="sb-lightbox-arrow sb-lightbox-arrow--prev" onClick={e => { e.stopPropagation(); lightboxPrev(); }}>‹</button>
          <motion.img
            key={lightboxIdx}
            src={AI_STORYBOARDS[lightboxIdx]}
            className="sb-lightbox-img"
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            onClick={e => e.stopPropagation()}
          />
          <button className="sb-lightbox-arrow sb-lightbox-arrow--next" onClick={e => { e.stopPropagation(); lightboxNext(); }}>›</button>
          <button className="sb-lightbox-close" onClick={() => setLightboxIdx(null)}>✕</button>
          <div className="sb-lightbox-counter">{lightboxIdx + 1} / {count}</div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Tool tabs — bottom of module, full width */}
    <div className="ai-gcell-footer ai-gcell-footer--module01">
      <div className="ai-tool-tabs">
        <span className="ai-tools-label">Tools I Use</span>
        {MODULE01_TOOLS.map((t, i) => (
          <span key={i} className="ai-tool-tab">
            {t.name}
          </span>
        ))}
      </div>
    </div>
    </>
  );
}

// ── AI Gallery data ───────────────────────────────────────────────────────────

const MODULE01_TOOLS = [
  { name: 'Claude',   desc: 'Creative brief articulation, concept framing, and strategic territory mapping.' },
  { name: 'ChatGPT',  desc: 'Rapid ideation, mood board copy, and visual direction scripting.' },
  { name: 'Gemini',   desc: 'Research, reference gathering, and cross-platform creative strategy.' },
];

const AI_GALLERY = [
  {
    step: '02',
    title: 'Image & Asset Creation',
    tools: [
      { name: 'ChatGPT Image',    desc: 'Campaign imagery and composites — commercially licensed, production-ready.' },
      { name: 'Nano Banana Pro',  desc: 'Concept-stage image generation and visual tone exploration.' },
      { name: 'Mid-Journey',      desc: 'Rapid format variation and iteration across asset sizes.' },
      { name: 'Adobe Firefly',    desc: 'Commercially safe generative imagery for brand and campaign use.' },
    ],
    img: '/ai-02-asset.webp',
    cls: 'half',
  },
  {
    step: '03',
    title: 'Brand System Scaling',
    tools: [
      { name: 'Elevenlabs',  desc: 'AI voiceover and audio generation for brand content at scale.' },
      { name: 'Higgsfield',  desc: 'AI video generation and motion content for brand campaigns.' },
      { name: 'Magnefic',    desc: 'Brand system adaptation and creative scaling across markets.' },
      { name: 'Figma',       desc: 'Layout scaling and component adaptation across format sizes.' },
      { name: 'Weavy',       desc: 'Visual direction, mood boarding, and creative territory setting.' },
    ],
    img: '/ai-03-brand.webp',
    cls: 'half portrait',
  },
  {
    step: '04',
    title: 'Motion & Video',
    tools: [
      { name: 'Veo',                desc: 'AI video generation for campaign and concept motion content.' },
      { name: 'Seedance',          desc: 'AI-driven motion and animation for brand storytelling.' },
      { name: 'Kling',             desc: 'Video generation and cinematic motion at scale.' },
      { name: 'Adobe After Effects', desc: 'Motion graphics, keyframing, and visual effects production.' },
      { name: 'CapCut',            desc: 'Fast-turnaround social video editing and format adaptation.' },
      { name: 'Adobe Premiere',    desc: 'Multi-format video editing and production pipeline.' },
    ],
    video: '/projects/hero.mp4',
    img: '/ai-04-motion.webp',
    cls: 'half',
  },
];

// ── AI Gallery Cell ───────────────────────────────────────────────────────────

function AIGalleryCell({ item, index }) {
  const { ref, inView } = useReveal('-6%');
  const [loaded, setLoaded] = useState(false);

  return (
    <motion.div
      ref={ref}
      className={`ai-gcell ai-gcell--${item.cls}`}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.72, delay: index * 0.07, ease: EASE }}
      whileHover={{ scale: 1.015, transition: { duration: 0.3, ease: EASE } }}
    >
      {/* Image / Video area */}
      <div className="ai-gcell-img-wrap">
        {item.video ? (
          <video
            preload="none"
            src={item.video}
            autoPlay loop muted playsInline
            className="ai-gcell-img"
            style={{ opacity: 1 }}
          />
        ) : (
          <>
            {!loaded && <div className={`ai-gcell-placeholder ai-gcell-ph--${index + 1}`} />}
            <img
              src={item.img}
              alt={item.title}
              className="ai-gcell-img"
              onLoad={() => setLoaded(true)}
              style={{ opacity: loaded ? 1 : 0 }}
            />
          </>
        )}
        <div className="ai-gcell-label">
          <span className="ai-gcell-name">{item.title}</span>
        </div>
        {!item.video && (
          <div className="ai-gcell-coming-soon">Coming Soon</div>
        )}
      </div>

      {/* Tools I Use — static pills */}
      {item.tools && (
        <div className="ai-gcell-footer">
          <div className="ai-tool-tabs">
            <span className="ai-tools-label">Tools I Use</span>
            {item.tools.map((t, i) => (
              <span key={i} className="ai-tool-tab">{t.name}</span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ── AI Tab ────────────────────────────────────────────────────────────────────

function AITab() {
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setMuted(vid.muted);
  };

  return (
    <div className="ai-tab">
      <div className="ai-video-wrap">
        <video
          preload="none"
          ref={videoRef}
          className="ai-video"
          src="/projects/AI Projects & Workflow/aI Hero.mp4"
          autoPlay
          loop
          muted
          playsInline
          onClick={toggleMute}
          style={{ cursor: 'pointer' }}
        />
      </div>

      <div className="ai-featured">
        <div className="ai-featured-glow" />
        <div className="ai-featured-body">
          <div className="ai-featured-top">
            <span className="ai-featured-label">AI-Assisted Creative Direction</span>
          </div>
          <div className="ai-featured-bottom">
            <h3 className="ai-featured-title">Not a prompt engineer. A director.</h3>
            <p className="ai-featured-desc">
              AI isn't free and it isn't effortless. The tools cost. The skill to use them
              well costs more. What you get is campaign-quality creative at a fraction of
              traditional production cost — with the brand integrity that only comes from
              experienced creative leadership.
            </p>
          </div>
        </div>
        <div className="ai-featured-preview">
          <img src="/projects/AI Projects & Workflow/AI_Workflow.webp" alt="AI Workflow" className="ai-preview-img" style={{ pointerEvents: 'none' }} loading="lazy" />
        </div>
      </div>

      {/* Bento gallery — Module 01 is the storyboard carousel, 02-04 are image cells */}
      <div className="ai-gallery">
        <div className="ai-gcell ai-gcell--full ai-gcell--module01">
          <div className="ai-gcell-step-tag">
            <span className="ai-gcell-name">Concept & Ideation</span>
          </div>
          <AIStoryboardCarousel />
        </div>
        {AI_GALLERY.filter(item => item.step !== '01' && item.step !== '04').map((item, i) => (
          <AIGalleryCell key={item.step} item={item} index={i + 1} />
        ))}
        <MotionVideoCell />
      </div>


    </div>
  );
}

// ── Clients ───────────────────────────────────────────────────────────────────

function Clients() {
  const { ref, inView } = useReveal();
  return (
    <div className="clients" ref={ref}>
      <div className="clients-inner">
      <motion.span
        className="clients-label"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.5 }}
      >
        Brands I've Shaped
      </motion.span>
      <div className="clients-list">
        {CLIENTS.map((c, i) => (
          <motion.span
            key={i}
            className="client-name"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: i * 0.07 }}
          >
            {c}
          </motion.span>
        ))}
      </div>
      </div>
    </div>
  );
}

// ── About ─────────────────────────────────────────────────────────────────────

function About() {
  const { ref, inView } = useReveal();
  return (
    <section className="about" ref={ref}>
      <div className="about-grid">
        <motion.div
          className="about-text"
          initial={{ opacity: 0, y: 28 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.82, delay: 0.2, ease: EASE }}
        >
          <p>
            I'm a Melbourne-based Art Director and Creative Lead with a track record
            built on global scale. Campaigns hitting retail shelves in 50+ markets.
            A Times Square campaign that earned its own audience without paid amplification. Brand systems spanning
            New York, trade show floors, and everything in between. My background sits
            at the intersection of strategy, craft, and production — I understand what
            it takes to get from brief to final frame, and I've done it across FMCG,
            entertainment, government, and consumer products.
          </p>
          <p>
            For a decade I led creative at Moose Toys as Art Director, Global Marketing
            — one of the world's fastest-growing toy companies. That meant directing
            campaigns for Magic Mixies, Little Live Pets, Shopkins, Pikmi Pops, and
            Bluey across broadcast, digital, retail POS, trade show, and experiential,
            simultaneously, in markets with different languages, retail landscapes,
            and cultural contexts. Back-to-back TOTY wins. Category-defining work.
          </p>
          <p>
            AI is now built into every stage of my process — not as a shortcut,
            as a genuine production multiplier. I use it to generate and pressure-test
            concepts before production begins, create and adapt assets at scale, and
            produce motion content that would otherwise take three times the resource.
            The creative direction still comes from 15 years of hard-won judgment.
            AI just means more of it, faster.
          </p>
          <div className="about-links">
            <div className="hero-btns-row">
              <a href="#work" className="btn-primary">View Work</a>
              <a
                href="https://linkedin.com/in/sainisahil"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="about-sidebar"
          initial={{ opacity: 0, y: 28 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.82, delay: 0.35, ease: EASE }}
        >
          <div className="sidebar-block">
            <p className="sidebar-heading">What I do</p>
            <ul className="skills-list">
              {SKILLS.map((s, i) => (
                <li key={i} className="skill-item">
                  <span className="skill-dot" />{s}
                </li>
              ))}
            </ul>
          </div>

          <div className="sidebar-block">
            <p className="sidebar-heading">Tools</p>
            <div className="sidebar-ai-tags">
              {[
                'After Effects', 'Premiere Pro', 'Photoshop',
                'Illustrator', 'InDesign', 'Adobe Firefly',
                'Figma', 'WordPress', 'Mailchimp', 'Google Workspace',
              ].map((tool, i) => (
                <span key={i} className="sidebar-tool-tag">{tool}</span>
              ))}
            </div>
          </div>

          <div className="sidebar-block">
            <p className="sidebar-heading sidebar-heading--ai">AI &amp; Emerging Tech</p>
            <div className="sidebar-ai-tags">
              {[
                'Midjourney', 'Adobe Firefly', 'Figma Weave',
                'Magnific', 'HeyGen', 'Higgsfield', 'Seedance',
                'ChatGPT', 'Claude', 'Gemini', 'Runway',
                'ElevenLabs', 'Kling',
              ].map((tool, i) => (
                <span key={i} className="sidebar-ai-tag">{tool}</span>
              ))}
            </div>
          </div>

        </motion.div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────

function Contact({ onContactClick }) {
  const { ref, inView } = useReveal();
  return (
    <section className="contact" id="contact" ref={ref}>
      <motion.span
        className="eyebrow"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.5 }}
      >
        Contact
      </motion.span>

      <motion.h2
        className="contact-heading"
        initial={{ opacity: 0, y: 28 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.78, delay: 0.15, ease: EASE }}
      >
        Let's Connect
        <span className="contact-heading-sub">& make something that creates a WOW!</span>
      </motion.h2>

      <motion.p
        className="contact-sub"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.78, delay: 0.28, ease: EASE }}
      >
        Open to senior creative leadership — Creative Lead, Head of Creative,
        Art Director — at a Melbourne brand, agency, or in-house team.
        Also available for freelance creative direction and production.
        If you want work that holds up, let's talk.
      </motion.p>

      <motion.div
        className="contact-btns"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.78, delay: 0.42, ease: EASE }}
      >
        <button className="btn-primary" onClick={() => onContactClick()}>
          Get in Touch
        </button>
        <a
          href="https://linkedin.com/in/sainisahil"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost"
        >
          LinkedIn
        </a>
      </motion.div>

      <motion.div
        className="contact-direct"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.58 }}
      >
        <span className="contact-direct-label">Or reach me directly</span>
        <a href="mailto:creative@sahilsaini.com.au" className="contact-direct-email">
          creative@sahilsaini.com.au
        </a>
        <a href="tel:+61415523815" className="contact-direct-phone">
          +61 415 523 815
        </a>
      </motion.div>
    </section>
  );
}

// ── Services ──────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    num: '01',
    title: 'Branding & Identity',
    desc: 'Visual systems built to earn recognition before a word is read — and hold together across every format, market, and medium.',
    deliverables: ['Logo & wordmark', 'Brand guidelines', 'Colour & type system', 'Icon & pattern library', 'Brand architecture'],
  },
  {
    num: '02',
    title: 'Digital & Social Media',
    isNew: true,
    desc: 'Feed-native content designed to stop the scroll — not repurposed from a TVC. Built for the platform from the first frame.',
    deliverables: ['Carousel design systems', 'Short-form video & Reels', 'Branded content & UGC ads', 'Story & highlight covers', 'Paid social creative (Meta, TikTok, Pinterest)'],
  },
  {
    num: '03',
    title: 'AI UGC Ads',
    isNew: true,
    desc: 'AI-generated performance creative that looks authentic, converts like a real person, and scales without a production crew.',
    deliverables: ['AI avatar scripts', 'Platform-native formats', 'Hook variations (A/B)', 'Voice & caption sync', 'Rapid turnaround'],
  },
  {
    num: '04',
    title: 'Campaign Art Direction',
    desc: 'From blank brief to final frame — creative concept, visual direction, and production oversight across every channel.',
    deliverables: ['Campaign concept', 'Key visual (KV)', 'Hero assets', 'Multi-channel rollout', 'Global adaptation'],
  },
  {
    num: '05',
    title: 'Point of Sale',
    desc: 'Shelf-commanding retail creative that earns the pick-up. Built around the product truth, the retail context, and the consumer moment.',
    deliverables: ['In-store displays', 'Retail graphics', 'Range systems', 'Print-ready files', 'Trade show assets'],
  },
  {
    num: '06',
    title: 'Motion & Video',
    desc: 'Creative direction and post-production across commercial, social, and brand formats — with AI accelerating every stage of delivery.',
    deliverables: ['Brand film', 'Motion graphics', 'Social video', 'After Effects animation', 'Video reformat pack'],
  },
  {
    num: '07',
    title: 'Retail & POS',
    desc: 'In-store creative that translates brand strategy into the moment of purchase — endcaps, standees, and environments that convert.',
    deliverables: ['Endcaps & standees', 'Shelf strips', 'Counter cards', 'In-store environments', 'Trade displays'],
  },
  {
    num: '08',
    title: 'Signage & OOH',
    desc: 'Large-format work designed for the physical world — not scaled from a social post. Built to stop traffic, not just fill space.',
    deliverables: ['Billboard design', 'Transit media', 'Event signage', 'Environmental design', 'Street furniture'],
  },
  {
    num: '09',
    title: 'Creative Consulting',
    desc: 'Senior creative direction, brand thinking, and team leadership — without the full-time overhead. Engagements from a single brief to ongoing partnership.',
    deliverables: ['Creative audits', 'Brand reviews', 'Team mentorship', 'Brief development', 'Strategic direction'],
  },
];

function Services({ onContactClick }) {
  const [selected, setSelected] = useState([]);
  const [showGrid, setShowGrid] = useState(false);
  const { ref, inView } = useReveal();

  const toggle = (title) => {
    setSelected(prev =>
      prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]
    );
  };

  const handleStartBrief = () => {
    const prefill = selected.length > 0
      ? `Hi Sahil,\n\nI'm interested in:\n${selected.map(s => `• ${s}`).join('\n')}\n\nHere's what I'm working on: `
      : '';
    onContactClick(prefill);
    setSelected([]);
  };

  return (
    <section className="services" id="services" ref={ref}>

      {/* ── Header ── */}
      <motion.div
        className="svc-intro"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: EASE }}
      >
        <span className="services-label">Work with me</span>
        <h2 className="svc-heading">Start a brief.</h2>
        <p className="svc-subtext">
          Pick what you need — or just send a note about your project. I'll scope it and respond within 24 hours.
        </p>
      </motion.div>

      {/* ── 3-step process ── */}
      <motion.div
        className="svc-process"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
      >
        {[
          { num: '01', title: 'Brief', desc: 'Select services below or send a quick outline of your project.' },
          { num: '02', title: 'Scope', desc: "I'll come back with approach and timeline within 24 hours." },
          { num: '03', title: 'Create', desc: 'We get moving.' },
        ].map((s, i) => (
          <div key={i} className="svc-process-step">
            <span className="svc-process-title">{s.title}</span>
            <p className="svc-process-desc">{s.desc}</p>
          </div>
        ))}
      </motion.div>

      {/* ── CTA ── */}
      <motion.button
        className="svc-open-btn"
        onClick={() => setShowGrid(g => !g)}
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
      >
        {showGrid ? 'Close' : 'View Services'}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: showGrid ? 'rotate(90deg)' : 'none', transition: 'transform 0.3s ease' }}>
          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
        </svg>
      </motion.button>

      {/* ── Expandable service grid ── */}
      <AnimatePresence>
        {showGrid && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            style={{ overflow: 'hidden' }}
          >
            <div className="svc-grid">
              {SERVICES.map((s, i) => {
                const isSelected = selected.includes(s.title);
                return (
                  <motion.button
                    key={i}
                    className={`svc-card${isSelected ? ' svc-card--on' : ''}${s.isNew ? ' svc-card--new' : ''}`}
                    onClick={() => toggle(s.title)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: i * 0.04, ease: EASE }}
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.985 }}
                    aria-pressed={isSelected}
                  >
                    <div className="svc-card-top">
                      <div className="svc-card-badges">
                        {s.isNew && <span className="svc-new-badge">New</span>}
                        <span className={`svc-check${isSelected ? ' svc-check--on' : ''}`}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                      </div>
                    </div>
                    <h3 className="svc-card-title">{s.title}</h3>
                    <p className="svc-card-desc">{s.desc}</p>
                    <ul className="svc-deliverables">
                      {s.deliverables.map((d, di) => (
                        <li key={di}>{d}</li>
                      ))}
                    </ul>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Brief tray — fixed bottom when services selected ── */}
      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div
            className="svc-tray"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.38, ease: EASE }}
          >
            <div className="svc-tray-inner">
              <div className="svc-tray-left">
                <span className="svc-tray-count">
                  {selected.length} {selected.length === 1 ? 'service' : 'services'} selected
                </span>
                <div className="svc-chips">
                  {selected.map(title => (
                    <button
                      key={title}
                      className="svc-chip"
                      onClick={() => toggle(title)}
                      aria-label={`Remove ${title}`}
                    >
                      {title}
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
              <button className="svc-tray-cta" onClick={handleStartBrief}>
                Start the Brief
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="footer">
      <span className="footer-logo">SS</span>
      <span className="footer-copy">© 2026 Sahil Saini · Art Director &amp; Creative Lead | ABN 52 654 639 602</span>
      <div className="footer-links">
        <a href="https://linkedin.com/in/sainisahil" target="_blank" rel="noopener noreferrer">LinkedIn</a>
      </div>
      <span className="footer-disclaimer">Some work shown as mockup recreations due to NDA. All creative direction, concept and design development is my own. Brand IP belongs to respective clients and employers.</span>
    </footer>
  );
}

// ── Motion Graphics ───────────────────────────────────────────────────────────

function MotionCard({ video, autoplay, isLoaded, hasAudio, isStopped, onPlay, onToggleAudio }) {
  const iframeRef = useRef(null);
  const [thumbSrc, setThumbSrc] = useState(
    `https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`
  );
  const [delayedLoad, setDelayedLoad] = useState(!video.startDelay);

  useEffect(() => {
    if (video.startDelay && isLoaded) {
      const timer = setTimeout(() => setDelayedLoad(true), video.startDelay);
      return () => clearTimeout(timer);
    }
  }, [isLoaded, video.startDelay]);

  const postCmd = (func, args = []) => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args }), '*'
    );
  };

  useEffect(() => {
    if (!isLoaded) return;
    // Set highest available quality first
    const qualityTimer = setTimeout(() => {
      postCmd('setPlaybackQuality', ['hd1080']);
    }, 1200);
    if (isStopped) {
      postCmd('pauseVideo');
      postCmd('mute');
    } else if (hasAudio) {
      postCmd('playVideo');
      postCmd('unMute');
    } else {
      postCmd('playVideo');
      postCmd('mute');
    }
    return () => clearTimeout(qualityTimer);
  }, [hasAudio, isStopped, isLoaded]);

  const embedUrl = `https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${video.youtubeId}&rel=0&modestbranding=1&color=white&enablejsapi=1&vq=hd1080&hd=1`;

  const AudioOverlay = () => (
    <button
      className={`mgcard-overlay${hasAudio ? ' mgcard-overlay--active' : ''}`}
      onClick={onToggleAudio}
      aria-label={hasAudio ? 'Mute video' : 'Play with audio'}
    >
      <span className="mgcard-audio-icon">
        {hasAudio ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <line x1="23" y1="9" x2="17" y2="15"/>
            <line x1="17" y1="9" x2="23" y2="15"/>
          </svg>
        )}
      </span>
    </button>
  );

  return (
    <div className="mgcard">
      <div className="mgcard-video">
        {isLoaded && delayedLoad ? (
          <>
            <iframe
              ref={iframeRef}
              className="mgcard-iframe"
              src={embedUrl}
              allow="autoplay; encrypted-media"
              allowFullScreen
              title={video.title}
              style={video.thumbScale ? { width: `${video.thumbScale}%`, height: `${video.thumbScale}%`, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' } : undefined}
            />
            <AudioOverlay />
          </>
        ) : (
          <>
            <img
              className="mgcard-thumb"
              src={thumbSrc}
              alt={video.title}
              onError={() => setThumbSrc(
                `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`
              )}
              onLoad={e => {
                // YouTube serves a tiny grey placeholder (120×90) instead of a 404
                // when maxresdefault doesn't exist — fall back if that's what loaded
                if (e.currentTarget.naturalWidth < 300) {
                  setThumbSrc(`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`);
                }
              }}
            />
            <div className="mgcard-thumb-overlay" />
            <button className="mgcard-play" onClick={onPlay} aria-label="Play video">
              <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                <polygon points="6,3 20,12 6,21" />
              </svg>
            </button>
          </>
        )}
      </div>

      <div className="mgcard-body">
        <span className="mgcard-brand">{video.brand}</span>
        <h3 className="mgcard-title">{video.title}</h3>
        <p className="mgcard-blurb">{video.blurb}</p>
      </div>
    </div>
  );
}

function MotionTab() {
  const [activeAudio, setActiveAudio]     = useState(null);
  const [manualPlaying, setManualPlaying] = useState(new Set());

  const handlePlay = (id) => {
    setManualPlaying(prev => new Set([...prev, id]));
  };

  const handleToggleAudio = (id) => {
    setActiveAudio(prev => (prev === id ? null : id));
  };

  return (
    <div className="motion-tab">
      <div className="motion-grid">
        {MOTION_VIDEOS.map((video, index) => {
          const autoplay  = index < 2 || !!video.autoplay;                       // first 2 + any flagged
          const isLoaded  = autoplay || manualPlaying.has(video.id);
          const isStopped = isLoaded && activeAudio !== null && activeAudio !== video.id;

          return (
            <MotionCard
              key={video.id}
              video={video}
              autoplay={autoplay}
              isLoaded={isLoaded}
              hasAudio={activeAudio === video.id}
              isStopped={isStopped}
              onPlay={() => handlePlay(video.id)}
              onToggleAudio={() => handleToggleAudio(video.id)}
            />
          );
        })}
      </div>
    </div>
  );
}


// ── Main Site ─────────────────────────────────────────────────────────────────

function MainSite() {
  const [contactOpen,    setContactOpen]    = useState(false);
  const [contactPrefill, setContactPrefill] = useState('');
  const openContact = (prefill = '') => {
    setContactPrefill(prefill);
    setContactOpen(true);
  };
  const closeContact = () => {
    setContactOpen(false);
    setTimeout(() => setContactPrefill(''), 450);
  };

  const openServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App">
      <Helmet>
        <title>Sahil Saini — Art Director & Creative Lead, Melbourne</title>
        <meta name="description" content="Sahil Saini is an Art Director and Creative Lead based in Melbourne with 15+ years experience across brand systems, campaigns, motion, and global markets." />
        <meta property="og:title" content="Sahil Saini — Art Director & Creative Lead, Melbourne" />
        <meta property="og:description" content="15+ years leading creative across brand systems, campaigns, motion, and 50+ global markets. Work includes Magic Mixies, Little Live Pets, Shopkins, Gui Gui and more." />
        <meta property="og:url" content="https://sahilsaini.com.au/" />
        <meta property="og:image" content="https://sahilsaini.com.au/logo512.webp" />
        <link rel="canonical" href="https://sahilsaini.com.au/" />
      </Helmet>
      <Nav onContactClick={openContact} onServicesClick={openServices} />
      <main>
        <Hero onContactClick={openContact} />
        <AwardsModule />
        <Work />
        <IntroReel />
        <About />
        <Clients />
        <Services onContactClick={openContact} />
        <Contact onContactClick={openContact} />
      </main>
      <Footer />
      <ContactModal open={contactOpen} onClose={closeContact} prefill={contactPrefill} />
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<MainSite />} />
            <Route path="/project/:slug" element={<ProjectPage />} />
            <Route path="/educomply" element={<EduComply />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
